// src/pages/EditPropertyPage.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../components/layout/Layout";
import { getPropertyById, updateProperty, getAddressById, lookupAddress } from "../api/propertyApi";
import { getApiErrorMessage } from "../utils/concurrency";

import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  Snackbar,
  CircularProgress,
  Collapse,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

const toDateInputValue = (value) => {
  if (value == null || value === "") return "";
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : "";
};

const MONTH_OPTIONS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

function EditPropertyPage() {
  const { propertyId } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    name: "",
    property_type: "",
    address_line1: "",
    address_line2: "",
    city: "",
    county: "",
    postcode: "",
    property_manager: "",
    freeholder_name: "",
    freeholder_address: "",
    rtm_rmc_type: "",
    rtm_rmc_name: "",
    company_number: "",
    utr_number: "",
    financial_year_start_day: "",
    financial_year_start_month: "",
    bank_name: "",
    account_number: "",
    sort_code: "",
    ideal_postcodes_id: "",
    uprn: "",
    address_latitude: "",
    address_longitude: "",
    take_on_date: "",
    live: true,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [lookupPostcode, setLookupPostcode] = useState("");
  const [lookupFilter, setLookupFilter] = useState("");
  const [lookupResults, setLookupResults] = useState([]);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState("");
  const [selectedLookup, setSelectedLookup] = useState("");
  const [rowVersion, setRowVersion] = useState(1);
  const hasRtmRmc = Boolean(form.rtm_rmc_type);

  // Load property details
  useEffect(() => {
    const loadProperty = async () => {
      try {
        const data = await getPropertyById(propertyId);
        setForm({
          name: data.name || "",
          property_type: data.property_type || "",
          address_line1: data.address_line1 || "",
          address_line2: data.address_line2 || "",
          city: data.city || "",
          county: data.county || "",
          postcode: data.postcode || "",
          property_manager: data.property_manager || "",
          freeholder_name: data.freeholder_name || "",
          freeholder_address: data.freeholder_address || "",
          rtm_rmc_type: data.rtm_rmc_type || "",
          rtm_rmc_name: data.rtm_rmc_name || "",
          company_number: data.company_number || "",
          utr_number: data.utr_number || "",
          financial_year_start_day: data.financial_year_start_day ?? "",
          financial_year_start_month: data.financial_year_start_month ?? "",
          bank_name: data.bank_name || "",
          account_number: data.account_number || "",
          sort_code: data.sort_code || "",
          ideal_postcodes_id: data.ideal_postcodes_id || "",
          uprn: data.uprn || "",
          address_latitude: data.address_latitude ?? "",
          address_longitude: data.address_longitude ?? "",
          take_on_date: toDateInputValue(data.take_on_date),
          live: data.live !== false, // Default to true if not set
        });
        setRowVersion(data.row_version || 1);
        setLookupPostcode(data.postcode || "");
      } catch (err) {
        setError("Unable to load property details.");
      } finally {
        setLoading(false);
      }
    };

    loadProperty();
  }, [propertyId]);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleManagementStructureChange = (e) => {
    const value = e.target.value === "Neither" ? "" : e.target.value;
    setForm((prev) => ({
      ...prev,
      rtm_rmc_type: value,
      rtm_rmc_name: value ? prev.rtm_rmc_name : "",
    }));
  };

  const handleLookup = async () => {
    setLookupError("");
    setLookupResults([]);
    setSelectedLookup("");
    const trimmed = lookupPostcode.trim();
    if (!trimmed) {
      setLookupError("Enter a postcode to search.");
      return;
    }
    setLookupLoading(true);
    try {
      const data = await lookupAddress(trimmed, lookupFilter.trim());
      const suggestions = data?.suggestions || [];
      setLookupResults(suggestions);
      if (!suggestions.length) {
        setLookupError("No addresses found for that postcode.");
      }
    } catch (err) {
      console.error("Address lookup error:", err);
      setLookupError(err?.message || "Address lookup failed.");
    } finally {
      setLookupLoading(false);
    }
  };

  const handleSelectLookup = async (value) => {
    setSelectedLookup(value);
    setLookupError("");
    const selected = lookupResults[Number(value)] || null;
    if (!selected?.id) return;
    setForm((prev) => ({
      ...prev,
      address_line1: "",
      address_line2: "",
      city: "",
      county: "",
      postcode: "",
      ideal_postcodes_id: "",
      uprn: "",
      address_latitude: "",
      address_longitude: "",
    }));
    try {
      const address = await getAddressById(selected.id);
      setForm((prev) => ({
        ...prev,
        address_line1: address.line_1 || prev.address_line1,
        address_line2: address.line_2 || prev.address_line2,
        city: address.town_or_city || address.locality || prev.city,
        county: address.county || prev.county,
        postcode: address.postcode || lookupPostcode.trim() || prev.postcode,
        ideal_postcodes_id: address.ideal_postcodes_id || selected.id,
        uprn: address.uprn || "",
        address_latitude: address.latitude ?? "",
        address_longitude: address.longitude ?? "",
      }));
    } catch (err) {
      console.error("Address get error:", err);
      setLookupError(err?.message || "Address lookup failed.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const financialYearStartDay =
        form.financial_year_start_day === ""
          ? null
          : Number.parseInt(form.financial_year_start_day, 10);
      const financialYearStartMonth =
        form.financial_year_start_month === ""
          ? null
          : Number(form.financial_year_start_month);

      if (
        form.financial_year_start_day !== "" &&
        !Number.isInteger(financialYearStartDay)
      ) {
        throw new Error("Financial year start day must be an integer.");
      }

      const payload = {
        ...form,
        financial_year_start_day: financialYearStartDay,
        financial_year_start_month: financialYearStartMonth,
        ideal_postcodes_id: form.ideal_postcodes_id || null,
        uprn: form.uprn || null,
        address_latitude: form.address_latitude || null,
        address_longitude: form.address_longitude || null,
        take_on_date: form.take_on_date || null,
        row_version: rowVersion,
      };
      const result = await updateProperty(propertyId, payload);
      if (result?.row_version) {
        setRowVersion(result.row_version);
      }
      setToast({
        open: true,
        message: "Property updated",
        severity: "success",
      });

      setTimeout(() => {
        navigate(`/properties/${propertyId}/details`);
      }, 1200);
    } catch (err) {
      setToast({
        open: true,
        message: getApiErrorMessage(err, "Failed to update property. Please try again."),
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <Box className="page-container" sx={{ textAlign: "center", mt: 4 }}>
          <CircularProgress size={32} />
          <Typography sx={{ mt: 1 }}>Loading property details...</Typography>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box
        className="page-container"
        sx={{ display: "flex", justifyContent: "center", mt: 4 }}
      >
        <Box sx={{ width: "100%", maxWidth: 600 }}>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            Edit Property
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Paper sx={{ p: 4, borderRadius: 2 }} elevation={2}>
            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Name"
                name="name"
                required
                value={form.name}
                onChange={handleChange}
                margin="normal"
                disabled={saving}
              />

              <FormControl fullWidth required margin="normal" disabled={saving}>
                <InputLabel>Property Type</InputLabel>
                <Select
                  name="property_type"
                  value={form.property_type}
                  onChange={handleChange}
                  label="Property Type"
                >
                  <MenuItem value="Block">Block</MenuItem>
                  <MenuItem value="Estate">Estate</MenuItem>
                </Select>
              </FormControl>

              <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 2 }}>
                Address Lookup
              </Typography>
              <Stack spacing={2} sx={{ mt: 1 }}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <TextField
                    label="Postcode"
                    value={lookupPostcode}
                    onChange={(e) => setLookupPostcode(e.target.value)}
                    disabled={saving || lookupLoading}
                    fullWidth
                  />
                  <TextField
                    label="Filter (optional)"
                    value={lookupFilter}
                    onChange={(e) => setLookupFilter(e.target.value)}
                    disabled={saving || lookupLoading}
                    fullWidth
                  />
                </Stack>
                <Button
                  variant="outlined"
                  onClick={handleLookup}
                  disabled={saving || lookupLoading}
                  startIcon={<SearchIcon />}
                  sx={{ alignSelf: "flex-start", minWidth: 180 }}
                >
                  {lookupLoading ? "Searching..." : "Find Address"}
                </Button>
                {lookupError && <Alert severity="error">{lookupError}</Alert>}
                <Collapse in={lookupResults.length > 0} timeout={300}>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    {lookupResults.length}{" "}
                    {lookupResults.length === 1 ? "address" : "addresses"} found.
                    Choose one from the dropdown.
                  </Typography>
                  <FormControl fullWidth sx={{ mt: 1 }}>
                    <InputLabel>Select Address</InputLabel>
                    <Select
                      value={selectedLookup}
                      label="Select Address"
                      onChange={(e) => handleSelectLookup(e.target.value)}
                    >
                      {lookupResults.map((addr, index) => {
                        const label = addr.address || [addr.id].filter(Boolean).join(", ");
                        return (
                          <MenuItem key={`${index}-${label}`} value={String(index)}>
                            {label}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Collapse>
              </Stack>

              <TextField
                fullWidth
                label="Address Line 1"
                name="address_line1"
                required
                value={form.address_line1}
                onChange={handleChange}
                margin="normal"
                disabled={saving}
              />

              <TextField
                fullWidth
                label="Address Line 2"
                name="address_line2"
                value={form.address_line2}
                onChange={handleChange}
                margin="normal"
                disabled={saving}
              />

              <TextField
                fullWidth
                label="City"
                name="city"
                required
                value={form.city}
                onChange={handleChange}
                margin="normal"
                disabled={saving}
              />

              <TextField
                fullWidth
                label="County"
                name="county"
                value={form.county}
                onChange={handleChange}
                margin="normal"
                disabled={saving}
              />

              <TextField
                fullWidth
                label="Postcode"
                name="postcode"
                required
                value={form.postcode}
                onChange={handleChange}
                margin="normal"
                disabled={saving}
              />

              <TextField
                fullWidth
                label="Property Manager"
                name="property_manager"
                value={form.property_manager}
                onChange={handleChange}
                margin="normal"
                disabled={saving}
              />

              <TextField
                fullWidth
                label="Take-on date"
                name="take_on_date"
                type="date"
                value={form.take_on_date}
                onChange={handleChange}
                margin="normal"
                disabled={saving}
                InputLabelProps={{ shrink: true }}
                helperText="When this property was onboarded."
              />

              <TextField
                fullWidth
                label="Freeholder Name"
                name="freeholder_name"
                value={form.freeholder_name}
                onChange={handleChange}
                margin="normal"
                disabled={saving}
              />

              <TextField
                fullWidth
                label="Freeholder Address"
                name="freeholder_address"
                value={form.freeholder_address}
                onChange={handleChange}
                margin="normal"
                disabled={saving}
                multiline
                minRows={3}
              />

              <FormControl fullWidth required margin="normal" disabled={saving}>
                <InputLabel>Management Structure</InputLabel>
                <Select
                  value={form.rtm_rmc_type || "Neither"}
                  onChange={handleManagementStructureChange}
                  label="Management Structure"
                >
                  <MenuItem value="Neither">Neither</MenuItem>
                  <MenuItem value="RTM">RTM</MenuItem>
                  <MenuItem value="RMC">RMC</MenuItem>
                </Select>
              </FormControl>

              <Collapse in={hasRtmRmc} timeout={300} unmountOnExit>
                <Stack spacing={2} sx={{ mt: 1 }}>
                  <TextField
                    fullWidth
                    label={`Name of the ${form.rtm_rmc_type}`}
                    name="rtm_rmc_name"
                    value={form.rtm_rmc_name}
                    onChange={handleChange}
                    required={hasRtmRmc}
                    disabled={saving}
                  />
                </Stack>
              </Collapse>

              <TextField
                fullWidth
                label="Company Number"
                name="company_number"
                value={form.company_number}
                onChange={handleChange}
                margin="normal"
                disabled={saving}
              />

              <TextField
                fullWidth
                label="UTR Number"
                name="utr_number"
                value={form.utr_number}
                onChange={handleChange}
                margin="normal"
                disabled={saving}
              />

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  label="Financial Year Start Day"
                  name="financial_year_start_day"
                  type="number"
                  value={form.financial_year_start_day}
                  onChange={handleChange}
                  disabled={saving}
                  inputProps={{ min: 1, max: 31, step: 1 }}
                />
                <FormControl fullWidth disabled={saving}>
                  <InputLabel>Financial Year Start Month</InputLabel>
                  <Select
                    name="financial_year_start_month"
                    value={form.financial_year_start_month}
                    onChange={handleChange}
                    label="Financial Year Start Month"
                  >
                    <MenuItem value="">
                      <em>Not set</em>
                    </MenuItem>
                    {MONTH_OPTIONS.map((month) => (
                      <MenuItem key={month.value} value={month.value}>
                        {month.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>

              <TextField
                fullWidth
                label="Bank Name"
                name="bank_name"
                value={form.bank_name}
                onChange={handleChange}
                margin="normal"
                disabled={saving}
              />

              <TextField
                fullWidth
                label="Account Number"
                name="account_number"
                value={form.account_number}
                onChange={handleChange}
                margin="normal"
                disabled={saving}
              />

              <TextField
                fullWidth
                label="Sort Code"
                name="sort_code"
                value={form.sort_code}
                onChange={handleChange}
                margin="normal"
                disabled={saving}
              />

              <Box sx={{ mt: 2, mb: 1 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.live}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, live: e.target.checked }))
                      }
                      disabled={saving}
                    />
                  }
                  label="Live"
                />
              </Box>

              <Button
                fullWidth
                type="submit"
                variant="contained"
                disabled={saving}
                sx={{
                  mt: 3,
                  py: 1.2,
                  fontWeight: 600,
                }}
              >
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </form>
          </Paper>
        </Box>
      </Box>
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
}

export default EditPropertyPage;
